import type { Weekday } from "../components/organisms/DeliverStory/types";
import type { ScheduleRefreshConfig } from "../components/organisms/ScheduleRefreshModal/ScheduleRefreshModal.types";
import { WEEKDAY_FULL } from "./scheduleConstants";
import { dayOfMonthFromIso } from "./scheduleTypes";

const unitLabel = (unit: ScheduleRefreshConfig["unit"], n: number): string => {
  if (unit === "day") return n === 1 ? "day" : "days";
  if (unit === "week") return n === 1 ? "week" : "weeks";
  return n === 1 ? "month" : "months";
};

/** One-line summary of the repeat interval for schedule history tables. */
export const formatScheduleRefreshFrequency = (
  config: ScheduleRefreshConfig,
): string => {
  const n = config.every;
  return `Every ${n} ${unitLabel(config.unit, n)}`;
};

const weekdayShort = (d: Weekday): string =>
  WEEKDAY_FULL[d].slice(0, 3);

/**
 * Frequency column for schedule history — matches design copy (Daily, Weekly (Mon, Thu), …).
 */
export const formatScheduleHistoryFrequency = (
  config: ScheduleRefreshConfig,
): string => {
  const n = config.every;
  if (config.unit === "day" && n === 1) return "Daily";
  if (config.unit === "day") return `Every ${n} days`;
  if (config.unit === "week" && n === 1) {
    if (config.repeatOn.length === 0) return "Weekly";
    const days = [...config.repeatOn].map(weekdayShort).join(", ");
    return `Weekly (${days})`;
  }
  if (config.unit === "week") return `Every ${n} weeks`;
  if (config.unit === "month" && n === 1) {
    const dom = dayOfMonthFromIso(config.startsOn);
    return dom != null ? `Monthly (day ${dom})` : "Monthly";
  }
  if (config.unit === "month") {
    const dom = dayOfMonthFromIso(config.startsOn);
    return dom != null
      ? `Every ${n} months (day ${dom})`
      : `Every ${n} months`;
  }
  return formatScheduleRefreshFrequency(config);
};

/** Wall-clock time for TIME column (12h with AM/PM). */
export const formatScheduleWallTime = (
  refreshHour: number | null,
): string => {
  if (refreshHour === null || refreshHour === undefined) return "—";
  const h12 = refreshHour % 12 === 0 ? 12 : refreshHour % 12;
  const ampm = refreshHour < 12 ? "AM" : "PM";
  return `${h12}:00 ${ampm}`;
};

const TZ_SHORT: Record<string, string> = {
  UTC: "UTC",
  "Asia/Kolkata": "IST",
  "America/Los_Angeles": "PST",
  "America/New_York": "EST",
  "Europe/London": "GMT",
};

/** Short timezone label for history table (UTC, IST, …). */
export const formatScheduleTimezoneShort = (timezone: string): string => {
  if (TZ_SHORT[timezone]) return TZ_SHORT[timezone];
  const tail = timezone.split("/").pop();
  return tail?.replace(/_/g, " ") ?? timezone;
};

export const formatScheduleRefreshType = (
  config: ScheduleRefreshConfig,
): string => (config.incrementalEnabled ? "Incremental" : "Full");

/**
 * Default schedule name when API does not provide one.
 */
export const deriveScheduleDisplayName = (
  config: ScheduleRefreshConfig,
): string => {
  if (config.unit === "day" && config.every === 1) return "Daily refresh";
  if (config.unit === "week" && config.every === 1) return "Weekly sync";
  if (config.unit === "month" && config.every === 1) {
    const dom = dayOfMonthFromIso(config.startsOn);
    return dom != null ? `Monthly sync (day ${dom})` : "Monthly sync";
  }
  return `${formatScheduleHistoryFrequency(config)} schedule`;
};

export type ScheduleLifecycleLabelStatus = "active" | "paused" | "cancelled";

const STATUS_LABELS: Record<ScheduleLifecycleLabelStatus, string> = {
  active: "Active",
  paused: "Paused",
  cancelled: "Cancelled",
};

export const formatScheduleLifecycleStatus = (
  status: ScheduleLifecycleLabelStatus,
): string => STATUS_LABELS[status];
